import { memo, useEffect, useMemo, useState } from 'react';
import { getClipThumbnailUrl } from '../Sessions/AssetsTable/AssetTableClipThumbnail/ClipPreviewUtils';
import { getBroadcastBackgroundImageUrl } from '../Sessions/utils';
import { Clip } from '@/domains/asset';
import { classnames } from '@/libs/utils';
import Icon from '@/components/atoms/Icon';

interface HomePageClipThumbnailProps {
  clip: Clip;
  isClipActive?: boolean;
  isClipUnseen?: boolean;
  layout?: 'default' | 'fullWidth';
}

enum ThumbnailState {
  Primary = 'primary',
  Fallback = 'fallback',
  Error = 'error'
}

const HomePageClipThumbnail = ({ clip, isClipUnseen, isClipActive, layout }: HomePageClipThumbnailProps) => {
  const primaryUrl = useMemo(() => getClipThumbnailUrl(clip), [clip]);
  const fallbackUrl = useMemo(() => getBroadcastBackgroundImageUrl(clip.content), [clip.content]);
  const isAudio = useMemo(() => clip.content?.media_type === 'AUDIO', [clip.content?.media_type]);
  const [thumbnailState, setThumbnailState] = useState<ThumbnailState>(ThumbnailState.Primary);
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(true);

  useEffect(() => {
    setThumbnailState(ThumbnailState.Primary);
    setIsThumbnailLoading(true);
  }, [primaryUrl]);

  const onPrimaryError = () => {
    setThumbnailState(ThumbnailState.Fallback);
  };

  const onFallbackError = () => {
    setIsThumbnailLoading(false);
    setThumbnailState(ThumbnailState.Error);
  };

  const onThumbnailLoaded = () => {
    setIsThumbnailLoading(false);
  };

  const activeThumbnailUrl = thumbnailState === ThumbnailState.Primary ? primaryUrl : fallbackUrl;

  return (
    <div
      className={classnames(
        'relative flex items-center justify-center overflow-hidden rounded-md bg-cover bg-center bg-no-repeat shadow-lg',
        {
          'aspect-square w-[62%]': clip.asset_metadata.size === 'SQUARE',
          'aspect-[9/16] w-[35%]': clip.asset_metadata.size === 'PORTRAIT',
          'aspect-video w-[85%]': clip.asset_metadata.size === 'LANDSCAPE' && layout === 'default',
          'aspect-video w-full !rounded-none': clip.asset_metadata.size === 'LANDSCAPE' && layout === 'fullWidth',
          '!border-none': isClipUnseen && !isClipActive,
          'h-full !shadow-none': layout === 'fullWidth'
        }
      )}
    >
      {thumbnailState !== ThumbnailState.Error ? (
        <div>
          <img
            data-testid="clip-preview-image"
            className={classnames('mx-auto transition-all duration-300', isThumbnailLoading ? 'blur-sm' : 'blur-0')}
            src={activeThumbnailUrl}
            alt="Preview"
            onLoad={onThumbnailLoaded}
            onError={thumbnailState === ThumbnailState.Primary ? onPrimaryError : onFallbackError}
          />
        </div>
      ) : (
        <div className="flex size-6 items-center justify-center rounded-full border border-slate-300">
          <Icon name={isAudio ? 'IconHeadphones' : 'IconVideo'} size={13} stroke={2} className="text-slate-400/70" />
        </div>
      )}
    </div>
  );
};

export default memo(HomePageClipThumbnail);
